"use client";

import { createContext, useContext, useState, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  hasTokenForEnv,
  migrateOldTokens,
  ENV_STORAGE_KEY,
  type Environment,
} from "../lib/auth-tokens";

export type { Environment };

interface EnvironmentContextType {
  environment: Environment;
  // Set when the user toggles to an env that has no token yet — triggers the
  // credential dialog. Null means no pending switch.
  pendingEnvironment: Environment | null;
  toggleEnvironment: () => void;
  // Called by the credential dialog after a successful login for pendingEnvironment.
  completePendingSwitch: () => void;
  // Called when the user dismisses the credential dialog without logging in.
  cancelPendingSwitch: () => void;
  // Reconciles this context's `environment` with the JWT's actual claim
  // right after a plain (non-pending-switch) login. Without this, a fresh
  // visit always defaults to PRODUCTION (see getInitialEnvironment), so a
  // partner logging in with a SANDBOX-only key (e.g. every bootstrap key —
  // the backend always issues those as SANDBOX) ends up with `environment`
  // out of sync with which token cookie actually holds their session. That
  // mismatch matters: logout() clears the cookie for `environment`, so an
  // out-of-sync value means the real session's cookie survives "logout".
  setActiveEnvironment: (env: Environment) => void;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(
  undefined,
);

function getInitialEnvironment(): Environment {
  if (typeof window === "undefined") return "PRODUCTION";
  migrateOldTokens();
  const stored = localStorage.getItem(ENV_STORAGE_KEY);
  if (stored === "SANDBOX" || stored === "PRODUCTION") return stored;
  // localStorage key missing (cleared or first visit) — infer from which
  // token cookie is present so we don't default to PRODUCTION when the
  // partner only has a sandbox token.
  return hasTokenForEnv("SANDBOX") ? "SANDBOX" : "PRODUCTION";
}

function persist(env: Environment): void {
  localStorage.setItem(ENV_STORAGE_KEY, env);
}

export function EnvironmentProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [environment, setEnvironment] = useState<Environment>(getInitialEnvironment);
  const [pendingEnvironment, setPendingEnvironment] = useState<Environment | null>(null);

  const activateEnvironment = (env: Environment) => {
    persist(env);
    setEnvironment(env);
    // All cached data is environment-specific — clear it so every hook
    // re-fetches against the new JWT on the next render.
    queryClient.clear();
  };

  const toggleEnvironment = () => {
    const target: Environment = environment === "SANDBOX" ? "PRODUCTION" : "SANDBOX";
    if (hasTokenForEnv(target)) {
      activateEnvironment(target);
    } else {
      // No token for the target env yet — ask for credentials via dialog.
      setPendingEnvironment(target);
    }
  };

  const completePendingSwitch = () => {
    if (pendingEnvironment) {
      activateEnvironment(pendingEnvironment);
      setPendingEnvironment(null);
    }
  };

  const cancelPendingSwitch = () => setPendingEnvironment(null);

  return (
    <EnvironmentContext.Provider
      value={{
        environment,
        pendingEnvironment,
        toggleEnvironment,
        completePendingSwitch,
        cancelPendingSwitch,
        setActiveEnvironment: activateEnvironment,
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
}

export function useEnvironment() {
  const ctx = useContext(EnvironmentContext);
  if (!ctx) throw new Error("useEnvironment must be used within an EnvironmentProvider");
  return ctx;
}
